import { parsePhoneNumber, type PhoneNumberTypes } from "awesome-phonenumber"

import type { Parseable } from "./parseable"
import { PhoneNumberCountryCode } from "./phoneNumberCountryCode"
import { PhoneNumberHolder } from "./phoneNumberHolder"
import { ValidationError, type ValidationIssue } from "./validation"

/**
 *  This is a class representing a phone number inside our system.
 */
export class PhoneNumber {
  private _number: string

  // @deprecated use _countryCode instead
  private _prefix: string

  /**
   * The country code of the phone number.
   */
  private _countryCode: PhoneNumberCountryCode | undefined

  private _nationalNotation: string
  private _type: PhoneNumberTypes
  private _typeIsMobile: boolean
  private _typeIsFixedLine: boolean
  private _isServiceNumber: boolean
  private _servicePrefix?: string
  private _serviceNumber?: string

  private static readonly SERVICE_PREFIXES = ["0800", "0900"]
  private static readonly COMPANY_PREFIXES = ["088", "085"] // https://nl.wikipedia.org/wiki/Bedrijfsnummer

  get number() {
    return this._number
  }

  /**
   * @deprecated use countryCode.prefix instead
   */
  get prefix() {
    return this._countryCode?.prefix ?? this._prefix
  }

  get countryCode() {
    return this._countryCode
  }

  get nationalNotation() {
    return this._nationalNotation
  }

  /**
   * Returns the phone number type (e.g., 'mobile', 'fixed-line', 'fixed-line-or-mobile', etc.)
   */
  getType(): PhoneNumberTypes {
    return this._type
  }

  /**
   * Returns true if the phone number is a mobile number.
   * Also returns true for 'fixed-line-or-mobile' types.
   */
  isMobile(): boolean {
    return this._typeIsMobile
  }

  /**
   * Returns true if the phone number is a fixed-line (landline) number.
   * Also returns true for 'fixed-line-or-mobile' types.
   */
  isFixedLine(): boolean {
    return this._typeIsFixedLine
  }

  /**
   * Returns true if the phone number is a Dutch service number (0800, 088, 0900).
   * Service numbers are neither mobile nor fixed-line numbers.
   */
  isServiceNumber(): boolean {
    return this._isServiceNumber
  }

  /**
   * Returns true if the phone number belongs to a country other than the
   * Netherlands (the application's home country). Useful when behavior should
   * differ for foreign numbers, e.g. relaxing SMS-delivery wait windows.
   */
  isInternational(): boolean {
    return this._countryCode?.countryCode !== "NL"
  }

  /**
   * Returns the service number prefix (e.g., "0900", "0800", "088") if this is
   * a service number. Returns undefined if this is not a service number.
   */
  getServicePrefix(): string | undefined {
    return this._servicePrefix
  }

  /**
   * Returns the service number without the prefix (e.g., for "0900-9229" returns "9229").
   * Returns undefined if this is not a service number.
   */
  getServiceNumber(): string | undefined {
    return this._serviceNumber
  }

  /**
   *  Normalizes phone number format before parsing
   */
  protected normalizePhoneNumber(input: string): string {
    // Trim and keep only digits plus a single leading plus sign
    // NOTE: This also fixes an issue where an undefined/empty string is passed
    // as the prefix (e.g. `undefined/0612...`)
    let sanitized = input
      .trim()
      .replace(/[^\d|+]/g, "")
      .replace(/^\++/, "+")

    // Convert international prefix "00" to "+"
    // If we have a number that starts with 00, then we should replace the "00"
    // with a "+" sign. The "awesome-phonenumber" library doesn't understand
    // this as it uses international notations and the "00" seems to be specific
    // to some European countries.
    if (sanitized.startsWith("00")) {
      sanitized = `+${sanitized.substring(2)}`
    }

    // If the number doesn't start with "+" nor "0" then it's a shortform number.
    // This is ok as we can assume it's from NL, but our library is not happy
    // with it.
    if (!sanitized.startsWith("+") && !sanitized.startsWith("0")) {
      sanitized = `0${sanitized}`
    }

    return sanitized
  }

  /**
   *  Create a PhoneNumber from a string, throws an error if not a phone number.
   *
   * @param input The input string to parse.
   * @param regionCode The region code to use for parsing. Use the wildcard "*" to accept any region.
   * @param strict Whether to throw an error if the input is not a valid phone number.
   */
  constructor(
    input: string,
    regionCode: string = "NL",
    strict = false,
    options: { allowForeignPhoneNumbers?: boolean; allowLandlinePhoneNumbers?: boolean } = {}
  ) {
    const sanitized = this.normalizePhoneNumber(input)

    const results = parsePhoneNumber(sanitized, {
      regionCode: regionCode === "*" ? undefined : regionCode,
    })

    // There are cases a number could be "possible", but not valid. This
    // is the case with satellite phones (think more Starlink than US military)
    // and VoIP eSIM numbers. They have special handling which is ok with carriers,
    // but also don't need to be specifically compliant with E.123 and E.164 standards.
    //
    // NOTE: We allow additional `strict` parsing, which will throw an error if the input
    // is not considered a valid phone number. This does mean that some Dutch numbers
    // ala `+31698765432` are considered *invalid* as they have a `9` as the first digit
    // and no carriers picked this up yet
    //
    // Demo environment to test: https://libphonenumber.appspot.com
    if (!results.possible || !results.number || (strict && !results.valid && !results.shortValid)) {
      throw new ValidationError([{ code: "invalid", message: "error.phoneNumber.invalid" }])
    }

    this._number = results.number.significant
    this._prefix = `+${results.countryCode}`
    this._nationalNotation = results.number.national

    this._countryCode = PhoneNumberCountryCode.parse(`+${results.countryCode}`, results.regionCode)

    // Extract number type
    this._type = results.type ?? "unknown"
    this._typeIsMobile = results.typeIsMobile ?? false
    this._typeIsFixedLine = results.typeIsFixedLine ?? false

    // Detect service numbers (these are not mobile or fixed line numbers)
    const servicePrefix = this.detectServicePrefix(this._nationalNotation)

    this._isServiceNumber = !!servicePrefix

    if (this._isServiceNumber && servicePrefix) {
      const parts = this.parseServiceNumberParts(this._nationalNotation, servicePrefix)
      this._servicePrefix = parts.prefix
      this._serviceNumber = parts.number
    }

    // Domain-level validation checks (only when options are provided)
    const { allowForeignPhoneNumbers = true, allowLandlinePhoneNumbers = true } = options
    const issues: ValidationIssue[] = []

    if (!allowForeignPhoneNumbers && this._countryCode?.countryCode !== regionCode) {
      issues.push({ code: "invalidRegion", message: "error.phoneNumber.invalidRegion", data: { region: regionCode } })
    }

    if (!allowLandlinePhoneNumbers && this._typeIsFixedLine && !this._typeIsMobile) {
      issues.push({ code: "landlineNotAllowed", message: "error.phoneNumber.landlineNotAllowed" })
    }

    if (issues.length > 0) {
      throw new ValidationError(issues)
    }
  }

  /**
   *  Parse a string or undefined into a PhoneNumber.
   *
   *  Passing the strict flag will enforce phone-number validity as reported by
   *  the awesome-parser (rejects numbers that are merely "possible" but not "valid").
   */
  static parse(
    input: PhoneNumber | string | undefined,
    regionCode: string = "NL",
    strict = false
  ): PhoneNumber | undefined {
    if (input instanceof PhoneNumber) {
      return input
    }

    if (!input) {
      return undefined
    }

    try {
      return new PhoneNumber(input, regionCode, strict)
    } catch {
      return undefined
    }
  }

  /**
   *  Similar to the parse method it will try to parse the input and
   *  return a `PhoneNumber` object. The difference is that it will also
   *  check if the input has any additional characters that aren't part
   *  of the phone number. If any are found it will return undefined.
   *
   *  @param strict Control whether the parsing should also be strict. By
   *  default this is currently `false` as we went from "no validation" to "some
   *  validation". So we'll have to see how this plays out (in phases)
   */
  static parseStrict(input: string, regionCode: string = "NL", strict = false): PhoneNumber | undefined {
    if (/[a-z]+/i.test(input)) {
      return undefined
    }

    return this.parse(input, regionCode, strict)
  }

  /**
   *  Create a PhoneNumber from an entity that has phonePrefix and phoneNumber properties.
   *  This is a convenience method for the common pattern of concatenating prefix and number.
   *
   *  @param entity An object with phonePrefix and phoneNumber properties
   *  @param regionCode The region code to use for parsing (default: "NL")
   *  @param strict Whether to enforce strict validation (default: false)
   *  @returns PhoneNumber instance or undefined if parsing fails or properties are missing
   */
  static fromEntity(entity: PhoneNumberHolder, regionCode: string = "NL", strict = false): PhoneNumber | undefined {
    if (!entity.phonePrefix || !entity.phoneNumber) {
      return undefined
    }

    const combined = `${entity.phonePrefix}${entity.phoneNumber}`
    return this.parse(combined, regionCode, strict)
  }

  /**
   *  Get a string representation of the phone number.
   */
  toString() {
    return `${this.prefix}${this._number}`
  }

  /**
   *  Create a copy of the phone number.
   */
  copy() {
    return new PhoneNumber(this.toString())
  }

  /**
   *  Check if the phone number is equal to another phone number.
   */
  isEqual(other: PhoneNumber | string): boolean {
    if (other instanceof PhoneNumber) {
      return this.toString() === other.toString()
    }

    try {
      return this.isEqual(new PhoneNumber(other))
    } catch {
      return false
    }
  }

  /**
   * Checks if the national notation starts with a known service prefix.
   */
  private detectServicePrefix(nationalNotation: string): string | undefined {
    const withoutSpaces = nationalNotation.replaceAll(" ", "")

    // Check 4-digit prefixes first (e.g., "0800", "0900")
    const fourDigitPrefix = withoutSpaces.substring(0, 4)
    if (PhoneNumber.SERVICE_PREFIXES.includes(fourDigitPrefix)) {
      return fourDigitPrefix
    }

    // Check 3-digit prefixes (e.g., "088", "085")
    const threeDigitPrefix = withoutSpaces.substring(0, 3)
    if (PhoneNumber.COMPANY_PREFIXES.includes(threeDigitPrefix)) {
      return threeDigitPrefix
    }

    return undefined
  }

  /**
   * Parses the service number parts from the national notation based on a
   * detected prefix.
   */
  private parseServiceNumberParts(nationalNotation: string, servicePrefix: string): { number: string; prefix: string } {
    const withoutSpaces = nationalNotation.replaceAll(" ", "")
    const prefixLength = servicePrefix.length

    // The number part is everything after the prefix
    const number = withoutSpaces.substring(prefixLength).trim()

    return { prefix: servicePrefix, number }
  }
}

const _parseable: Parseable<PhoneNumber> = PhoneNumber
void _parseable
